import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import InputMask from "react-input-mask";
import { Save, Search } from "lucide-react";
import {
  atualizarRevendaDistribuidor,
  criarRevendaDistribuidor,
  listarRevendasDistribuidor,
  listarTabelasDistribuidor,
} from "../services/api";
import { getProdutosAtivos } from "../utils/produtosRevenda";

const soDigitos = (v) => String(v || "").replace(/\D/g, "");

const inputClass = `
  w-full px-3 py-2.5 rounded-xl border outline-none transition
  zen-card border-gray-300 text-gray-900 placeholder:text-gray-400
  focus:ring-2 focus:ring-blue-500/60
  dark:bg-slate-950/40 dark:border-white/10 dark:text-white dark:placeholder:text-slate-400
  dark:focus:ring-blue-400/40
`;
const labelClass = "block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1";

const vazio = {
  ativo: true,
  cnpj: "",
  inscricao_estadual: "",
  razao_social: "",
  nome_fantasia: "",
  logradouro: "",
  numero: "",
  complemento: "",
  bairro: "",
  cep: "",
  cidade: "",
  estado: "",
  email: "",
  telefone: "",
  simples_nacional: true,
  tabela_preco_id: "",
  exibir_precos: false,
  produto_zen_pdv: true,
  produto_zen_tag: true,
  produto_zen_gestao: true,
  produto_zen_mobile: true,
  produto_zen_pass: true,
};

const FormularioRevendaDistribuidor = ({ token }) => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [revenda, setRevenda] = useState(vazio);
  const [tabelas, setTabelas] = useState([]);
  const [loadingCnpj, setLoadingCnpj] = useState(false);
  const [saving, setSaving] = useState(false);
  const produtos = getProdutosAtivos();
  const produtosRevenda = [
    { name: "produto_zen_pdv", label: "ZenPDV Desktop", ativo: produtos.produtoZenPdv },
    { name: "produto_zen_tag", label: "ZenTag", ativo: produtos.produtoZenTag },
    { name: "produto_zen_gestao", label: "ZenGestão", ativo: produtos.produtoZenGestao },
    { name: "produto_zen_mobile", label: "ZenMobile", ativo: produtos.produtoZenMobile },
    { name: "produto_zen_pass", label: "ZenPass", ativo: produtos.produtoZenPass },
  ];

  useEffect(() => {
    listarTabelasDistribuidor(token)
      .then((res) => setTabelas(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTabelas([]));
  }, [token]);

  useEffect(() => {
    if (!isEdit) return;
    listarRevendasDistribuidor(token)
      .then((res) => {
        const atual = (Array.isArray(res.data) ? res.data : []).find((r) => String(r.id) === String(id));
        if (!atual) return;
        setRevenda({
          ativo: atual.ativo !== false,
          cnpj: atual.cnpj || "",
          inscricao_estadual: atual.inscricaoEstadual || "",
          razao_social: atual.razaoSocial || "",
          nome_fantasia: atual.nomeFantasia || "",
          logradouro: atual.logradouro || "",
          numero: atual.numero || "",
          complemento: atual.complemento || "",
          bairro: atual.bairro || "",
          cep: atual.cep || "",
          cidade: atual.cidade || "",
          estado: atual.estado || "",
          email: atual.email || "",
          telefone: atual.telefone || "",
          simples_nacional: atual.simplesNacional ?? true,
          tabela_preco_id: atual.tabelaPrecoId ? String(atual.tabelaPrecoId) : "",
          exibir_precos: !!atual.exibirPrecos,
          produto_zen_pdv: atual.produtoZenPdv !== false && produtos.produtoZenPdv,
          produto_zen_tag: atual.produtoZenTag !== false && produtos.produtoZenTag,
          produto_zen_gestao: atual.produtoZenGestao !== false && produtos.produtoZenGestao,
          produto_zen_mobile: atual.produtoZenMobile !== false && produtos.produtoZenMobile,
          produto_zen_pass: atual.produtoZenPass !== false && produtos.produtoZenPass,
        });
      })
      .catch((e) => console.error(e));
  }, [id, isEdit, token, produtos.produtoZenPdv, produtos.produtoZenTag, produtos.produtoZenGestao, produtos.produtoZenMobile, produtos.produtoZenPass]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRevenda((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleBuscarCNPJ = async () => {
    const cnpj = soDigitos(revenda.cnpj);
    if (cnpj.length !== 14) {
      alert("Informe um CNPJ válido.");
      return;
    }
    setLoadingCnpj(true);
    try {
      const res = await fetch(`https://publica.cnpj.ws/cnpj/${cnpj}`);
      const data = await res.json();
      const est = data?.estabelecimento;
      setRevenda((prev) => ({
        ...prev,
        razao_social: data?.razao_social || prev.razao_social,
        nome_fantasia: est?.nome_fantasia || prev.nome_fantasia,
        logradouro: est?.logradouro || prev.logradouro,
        numero: est?.numero || prev.numero,
        complemento: est?.complemento || prev.complemento,
        bairro: est?.bairro || prev.bairro,
        cep: est?.cep || prev.cep,
        cidade: est?.cidade?.nome || prev.cidade,
        estado: est?.estado?.sigla || prev.estado,
        email: est?.email || prev.email,
        telefone: est?.telefone1 || prev.telefone,
        inscricao_estadual: est?.inscricoes_estaduais?.[0]?.inscricao_estadual || prev.inscricao_estadual,
        simples_nacional: data?.simples?.simples === "Sim",
      }));
    } catch (err) {
      console.error(err);
      alert("Erro ao buscar CNPJ. Tente novamente.");
    } finally {
      setLoadingCnpj(false);
    }
  };

  const salvar = async (e) => {
    e.preventDefault();
    const cnpj = soDigitos(revenda.cnpj);
    if (cnpj.length !== 14) {
      alert("Informe um CNPJ válido com 14 dígitos.");
      return;
    }
    const payload = {
      ativo: Boolean(revenda.ativo),
      razaoSocial: (revenda.razao_social || "").trim(),
      nomeFantasia: (revenda.nome_fantasia || "").trim(),
      cnpj,
      logradouro: (revenda.logradouro || "").trim(),
      numero: (revenda.numero || "").trim(),
      complemento: (revenda.complemento || "").trim(),
      bairro: (revenda.bairro || "").trim(),
      cidade: (revenda.cidade || "").trim(),
      estado: (revenda.estado || "").trim().toUpperCase(),
      cep: soDigitos(revenda.cep),
      email: (revenda.email || "").trim(),
      telefone: soDigitos(revenda.telefone),
      inscricaoEstadual: (revenda.inscricao_estadual || "").trim(),
      simplesNacional: !!revenda.simples_nacional,
      tabelaPrecoId: revenda.tabela_preco_id ? Number(revenda.tabela_preco_id) : null,
      exibirPrecos: !!revenda.exibir_precos,
      produtoZenPdv: !!revenda.produto_zen_pdv && produtos.produtoZenPdv,
      produtoZenTag: !!revenda.produto_zen_tag && produtos.produtoZenTag,
      produtoZenGestao: !!revenda.produto_zen_gestao && produtos.produtoZenGestao,
      produtoZenMobile: !!revenda.produto_zen_mobile && produtos.produtoZenMobile,
      produtoZenPass: !!revenda.produto_zen_pass && produtos.produtoZenPass,
    };
    setSaving(true);
    try {
      if (isEdit) await atualizarRevendaDistribuidor(id, payload, token);
      else await criarRevendaDistribuidor({ revenda: payload }, token);
      navigate("/revendas");
    } catch (err) {
      const data = err?.response?.data;
      alert(typeof data === "string" ? data : data?.message || "Erro ao salvar a revenda.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="zen-card rounded-2xl p-6 md:p-8 shadow-sm dark:backdrop-blur">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
            {isEdit ? "Editar Revenda" : "Cadastro de Revenda"}
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-300/80 mt-1">
            Preencha os dados para {isEdit ? "editar a" : "cadastrar uma nova"} revenda.
          </p>
        </div>

        <form onSubmit={salvar} className="space-y-6">
          <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <label className={labelClass}>CNPJ</label>
                <div className="flex">
                  <InputMask mask="99.999.999/9999-99" name="cnpj" value={revenda.cnpj} onChange={handleChange} className={`${inputClass} rounded-r-none`} required />
                  <button type="button" onClick={handleBuscarCNPJ} disabled={loadingCnpj} className="px-4 py-2.5 rounded-r-xl font-semibold bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-60 inline-flex items-center">
                    <Search size={18} />
                  </button>
                </div>
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Inscrição Estadual</label>
                <input name="inscricao_estadual" value={revenda.inscricao_estadual} onChange={handleChange} className={inputClass} />
              </div>
              <div className="md:col-span-3">
                <label className={labelClass}>Razão Social</label>
                <input name="razao_social" value={revenda.razao_social} onChange={handleChange} className={inputClass} required />
              </div>
              <div className="md:col-span-3">
                <label className={labelClass}>Nome Fantasia</label>
                <input name="nome_fantasia" value={revenda.nome_fantasia} onChange={handleChange} className={inputClass} required />
              </div>
            </div>
          </div>

          <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-3">
                <label className={labelClass}>Logradouro</label>
                <input name="logradouro" value={revenda.logradouro} onChange={handleChange} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Número</label>
                <input name="numero" value={revenda.numero} onChange={handleChange} className={inputClass} required />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Complemento</label>
                <input name="complemento" value={revenda.complemento} onChange={handleChange} className={inputClass} />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Bairro</label>
                <input name="bairro" value={revenda.bairro} onChange={handleChange} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>CEP</label>
                <InputMask mask="99999-999" name="cep" value={revenda.cep} onChange={handleChange} className={inputClass} required />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Cidade</label>
                <input name="cidade" value={revenda.cidade} onChange={handleChange} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Estado</label>
                <input name="estado" value={revenda.estado} onChange={handleChange} className={inputClass} required />
              </div>
            </div>
          </div>

          <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Email</label>
                <input type="email" name="email" value={revenda.email} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Telefone</label>
                <InputMask mask="(99) 99999-9999" name="telefone" value={revenda.telefone} onChange={handleChange} className={inputClass} />
              </div>
              <div className="md:col-span-2 border-t border-gray-100 dark:border-white/10 pt-4">
                <p className="text-sm font-semibold text-gray-800 dark:text-slate-100 mb-3">Licenciamento</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Tabela de preço</label>
                    <select name="tabela_preco_id" value={revenda.tabela_preco_id} onChange={handleChange} className={inputClass}>
                      <option value="">— Nenhuma —</option>
                      {tabelas.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Tabela deste distribuidor, usada para cobrar a revenda.</p>
                  </div>
                  <div className="flex items-end pb-2">
                    <label className="inline-flex items-center gap-2">
                      <input type="checkbox" name="exibir_precos" checked={!!revenda.exibir_precos} onChange={handleChange} className="h-4 w-4 rounded" />
                      <span className="text-sm font-medium text-gray-700 dark:text-slate-200">Revenda pode ver preços no portal</span>
                    </label>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-sm font-semibold text-gray-800 dark:text-slate-100 mb-2">Produtos ativos</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">Somente os produtos liberados para este distribuidor.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {produtosRevenda.filter((p) => p.ativo).map((p) => (
                      <label key={p.name} className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-white/10 px-3 py-2">
                        <input type="checkbox" name={p.name} checked={!!revenda[p.name]} onChange={handleChange} className="h-4 w-4 rounded" />
                        <span className="text-sm font-medium">{p.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="md:col-span-2 flex flex-wrap items-center gap-6">
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" name="simples_nacional" checked={!!revenda.simples_nacional} onChange={handleChange} className="h-4 w-4 rounded" />
                  <span className="text-sm font-medium">Simples Nacional</span>
                </label>
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" name="ativo" checked={!!revenda.ativo} onChange={handleChange} className="h-4 w-4 rounded" />
                  <span className="text-sm font-medium">Revenda ativa</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm">
              <Save size={18} /> {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormularioRevendaDistribuidor;
